// File: frontend/src/components/songEdit/forms/MainSongForm.tsx
import React from 'react';
import { SongFormData } from '../types/songEdit';
import { BasicSongInfo } from '../fields/BasicSongInfo';
import { ComposerFields } from '../fields/ComposerFields';
import { TuneFields } from '../fields/TuneFields';
import { TagsField } from '../fields/TagsField';
import { MetadataFields } from '../fields/MetadataFields';
import { useSongForm } from '../hooks/useSongForm';
import { SONG_COLLECTIONS, isInCollection } from '../../../types/songCollections';

interface MainSongFormProps {
  formData: SongFormData;
  onChange: (formData: SongFormData) => void;
}

export const MainSongForm: React.FC<MainSongFormProps> = ({ formData, onChange }) => {
  const {
    handleInputChange,
    updateComposer,
    updateLyricist,
    handleTagsChange,
  } = useSongForm(formData, onChange);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...formData,
      language: e.target.value
    });
  };

  return (
    <div className="song-form">
      <BasicSongInfo
        title={formData.title}
        onChange={handleInputChange}
      />

      {/* Language Selection */}
      <div className="form-group mb-3">
        <label htmlFor="language" className="form-label">Language</label>
        <select
          id="language"
          className="form-select"
          value={formData.language || 'en'}
          onChange={handleLanguageChange}
          style={{ maxWidth: '200px' }}
        >
          <option value="en">English</option>
          <option value="es">Español (Spanish)</option>
        </select>
      </div>

      <ComposerFields
        composer={formData.composerData || null}
        lyricist={formData.lyricistData || null}
        onComposerChange={updateComposer}
        onLyricistChange={updateLyricist}
      />

      <TuneFields
        primaryTune={formData.primaryTune}
        alternateTunes={formData.alternateTunes}
        onPrimaryTuneChange={(tune) => {
          const newData = {
            ...formData,
            primaryTune: tune?.tune_name || '',
            tune_id: tune?.tune_id,
            tuneData: tune || undefined
          };
          onChange(newData);
        }}
        onAlternateTuneAdd={(tune) => {
          console.log('[MainSongForm] Adding alternate tune:', tune);
          const newTunes = [...formData.alternateTunes, tune];
          const newTuneIds = newTunes.map(t => t.tune_id).filter((id): id is number => id !== undefined);
          console.log('[MainSongForm] New alternate tunes:', newTunes);
          console.log('[MainSongForm] New tune_ids:', newTuneIds);
          const updatedFormData = {
            ...formData,
            alternateTunes: newTunes,
            tune_ids: newTuneIds
          };
          console.log('[MainSongForm] Updated form data:', updatedFormData);
          onChange(updatedFormData);
        }}
        onAlternateTuneRemove={(index) => {
          const newTunes = formData.alternateTunes.filter((_, i) => i !== index);
          const newTuneIds = newTunes.map(t => t.tune_id).filter((id): id is number => id !== undefined);
          onChange({
            ...formData,
            alternateTunes: newTunes,
            tune_ids: newTuneIds
          });
        }}
      />

      <fieldset className="song-form__group mb-3">
        <legend className="song-form__label fs-6">Collections</legend>
        <p className="text-muted small mb-2">Choose where this piece appears in Chants &amp; Circle Songs.</p>
        {SONG_COLLECTIONS.map(collection => (
          <div className="form-check form-check-inline" key={collection.id}>
            <input id={`collection-${collection.id}`} type="checkbox" className="form-check-input"
              checked={isInCollection(formData.tags, collection.tag)}
              onChange={event => {
                const tags = formData.tags.filter(tag => !isInCollection([tag], collection.tag));
                handleTagsChange(event.target.checked ? [...tags, collection.tag] : tags);
              }} />
            <label className="form-check-label" htmlFor={`collection-${collection.id}`}>{collection.label}</label>
          </div>
        ))}
      </fieldset>

      <TagsField
        tags={formData.tags}
        onAddTag={(tag) => handleTagsChange([...formData.tags, tag])}
        onRemoveTag={(index) => {
          const newTags = formData.tags.filter((_, i) => i !== index);
          handleTagsChange(newTags);
        }}
      />

      <MetadataFields
        videoLink={formData.video_link || ''}
        copyright={formData.copyright || ''}
        onChange={handleInputChange}
      />
    </div>
  );
};

export default MainSongForm;